// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { Account } from './account.js';
import { Bip39Mnemonic } from './bip39-mnemonic.js';
import { nanoCKD } from './bip32-key-derivation.js';
import { ADDRESS_GAP, SEED_LENGTH_BIP44, SEED_LENGTH_BLAKE2B } from './constants.js';
import { bytes, dec } from './convert.js';
import { Entropy } from './entropy.js';
import { Node } from './node.js';
import { Safe } from './safe.js';
import Tools from './tools.js';
/**
* Represents a wallet containing numerous Nano accounts derived from a single
* source, the form of which can vary based on the type of wallet. The Wallet
* class itself is abstract and cannot be directly instantiated. Currently, three
* types of wallets are supported, each as a derived class: Bip44Wallet,
* Blake2bWallet, LedgerWallet.
*/
class Wallet {
    #accounts;
    #id;
    #locked = true;
    #mnemonic;
    #safe;
    #seed;
    get id() { return this.#id.hex; }
    get isLocked() { return this.#locked; }
    get isUnlocked() { return !this.#locked; }
    get mnemonic() {
        if (this.#mnemonic instanceof Bip39Mnemonic) {
            return this.#mnemonic.phrase;
        }
        return '';
    }
    get seed() {
        if (typeof this.#seed === 'string') {
            return this.#seed;
        }
        return '';
    }
    constructor(seed, mnemonic, id) {
        if (this.constructor === Wallet) {
            throw new Error('Wallet is an abstract class and cannot be instantiated directly.');
        }
        this.#accounts = [];
        this.#id = id
            ? new Entropy(id)
            : new Entropy(16);
        this.#mnemonic = mnemonic ?? null;
        this.#safe = new Safe();
        this.#seed = seed ?? null;
    }
    /**
    * Retrieves accounts from a wallet using its child key derivation function.
    *
    * @param {number} from - Start index of secret keys. Default: 0
    * @param {number} to - End index of secret keys. Default: `from`
    */
    async accounts(from = 0, to = from) {
        if (from > to) {
            const swap = from;
            from = to;
            to = swap;
        }
        const accounts = [];
        for (let i = from; i <= to; i++) {
            if (this.#accounts[i]) {
                accounts.push(this.#accounts[i]);
            }
            else {
                const account = await this.ckd(i);
                if (account != null) {
                    this.#accounts[i] = account;
                    accounts.push(account);
                }
            }
        }
        return accounts;
    }
    /**
    * Fetches the lowest-indexed unopened account from a wallet in sequential
    * order. An account is unopened if it has no frontier block.
    *
    * @param {Node|string|URL} node - Node information required to refresh accounts, calculate PoW, and process blocks
    * @param {number} batchSize - Number of accounts to fetch and check per RPC callout
    * @param {number} from - Account index from which to start the search
    * @returns {Promise<Account>} The lowest-indexed unopened account belonging to the wallet
    */
    async getNextNewAccount(node, batchSize = ADDRESS_GAP, from = 0) {
        if (!Number.isSafeInteger(batchSize) || batchSize < 1) {
            throw new RangeError(`Invalid batch size ${batchSize}`);
        }
        const accounts = await this.accounts(from, from + batchSize - 1);
        const addresses = accounts.map(({ address }) => address);
        const data = {
            "accounts": addresses
        };
        const { errors } = await node.call('accounts_frontiers', data);
        for (const key of Object.keys(errors ?? {})) {
            const value = errors[key];
            if (value === 'Account not found') {
                return new Account(key);
            }
        }
        return await this.getNextNewAccount(node, batchSize, from + batchSize);
    }
    /**
    * Refreshes wallet account balances, frontiers, and representatives from the current state on the network.
    *
    * A successful response will set these properties on each account.
    *
    * @param {Node|string|URL} node - Node information required to refresh accounts, calculate PoW, and process blocks
    * @returns {Promise<Account[]>} Accounts with updated balances, frontiers, and representatives
    */
    async refresh(node, from = 0, to = from) {
        if (typeof node === 'string' || node.constructor === URL) {
            node = new Node(node);
        }
        if (node.constructor !== Node) {
            throw new TypeError('RPC must be a valid node');
        }
        const accounts = await this.accounts(from, to);
        for (let i = 0; i < accounts.length; i++) {
            try {
                await accounts[i].refresh(node);
            }
            catch (err) {
                accounts.splice(i--, 1);
            }
        }
        return accounts;
    }
    async lock(passkey) {
        let success = true;
        try {
            const data = {
                id: this.id,
                mnemonic: null,
                seed: null
            };
            if (this.#mnemonic instanceof Bip39Mnemonic) {
                data.mnemonic = this.#mnemonic.phrase;
            }
            if (typeof this.#seed === 'string') {
                data.seed = this.#seed;
            }
            success &&= await this.#safe.put(this.id, passkey, data);
            await Promise.all(this.#accounts.map((a) => a.lock(passkey)));
            if (!success) {
                throw null;
            }
        }
        catch (err) {
            throw new Error('Failed to lock wallet');
        }
        this.#locked = true;
        this.#mnemonic = null;
        this.#seed = null;
        return true;
    }
    async unlock(passkey) {
        try {
            const { id, mnemonic, seed } = await this.#safe.get(this.id, passkey);
            if (id !== this.id) {
                throw null;
            }
            await Promise.all(this.#accounts.map((a) => a.unlock(passkey)));
            if (mnemonic != null) {
                this.#mnemonic = await Bip39Mnemonic.fromPhrase(mnemonic);
            }
            if (seed != null) {
                this.#seed = seed;
            }
            this.#locked = false;
        }
        catch (err) {
            throw new Error('Failed to unlock wallet');
        }
        return true;
    }
}
/**
* Hierarchical deterministic (HD) wallet created by using a source of entropy to
* derive a mnemonic phrase. The mnemonic phrase, in combination with an optional
* salt, is used to generate a seed. A value can be provided as a parameter for
* entropy, mnemonic + salt, or seed; if no argument is passed, a new entropy
* value will be generated using a cryptographically strong pseudorandom number
* generator.
*
* Importantly, the salt is not stored in the instantiated Wallet object. If a
* salt is used, then losing it means losing the ability to regenerate the seed
* from the mnemonic.
*
* Accounts are derived from the seed. Private keys are derived using a BIP-44
* derivation path. The public key is derived from the private key using the
* Ed25519 key algorithm. Account addresses are derived as described in the nano
* documentation (https://docs.nano.org)
*
* A password must be provided when creating or importing the wallet and is used
* to lock and unlock the wallet. The wallet will be initialized as locked. When
* the wallet is unlocked, a new password can be specified using the lock()
* method.
*/
export class Bip44Wallet extends Wallet {
    static #isInternal = false;
    constructor(seed, mnemonic, id) {
        if (!Bip44Wallet.#isInternal) {
            throw new Error(`Bip44Wallet cannot be instantiated directly. Use 'await Bip44Wallet.create()' instead.`);
        }
        super(seed, mnemonic, id);
        Bip44Wallet.#isInternal = false;
    }
    static async create(passkey, salt = '') {
        Bip44Wallet.#isInternal = true;
        try {
            const e = new Entropy();
            return await Bip44Wallet.fromEntropy(passkey, e.hex, salt);
        }
        catch (err) {
            throw new Error(`Error creating new Bip44Wallet: ${err}`);
        }
    }
    static async fromEntropy(passkey, entropy, salt = '') {
        Bip44Wallet.#isInternal = true;
        try {
            const e = new Entropy(entropy);
            const m = await Bip39Mnemonic.fromEntropy(e.hex);
            const s = await m.toBip39Seed(salt);
            const wallet = new this(s, m);
            await wallet.lock(passkey);
            return wallet;
        }
        catch (err) {
            throw new Error(`Error importing Bip44Wallet from entropy: ${err}`);
        }
    }
    static async fromMnemonic(passkey, mnemonic, salt = '') {
        Bip44Wallet.#isInternal = true;
        try {
            const m = await Bip39Mnemonic.fromPhrase(mnemonic);
            const s = await m.toBip39Seed(salt);
            const wallet = new this(s, m);
            await wallet.lock(passkey);
            return wallet;
        }
        catch (err) {
            throw new Error(`Error importing Bip44Wallet from mnemonic: ${err}`);
        }
    }
    static async fromSeed(passkey, seed) {
        Bip44Wallet.#isInternal = true;
        if (seed.length !== SEED_LENGTH_BIP44) {
            throw new Error(`Expected a ${SEED_LENGTH_BIP44}-character seed, but received ${seed.length}-character string.`);
        }
        if (!/^[0-9a-fA-F]+$/i.test(seed)) {
            throw new Error('Seed contains invalid hexadecimal characters.');
        }
        const wallet = new this(seed);
        await wallet.lock(passkey);
        return wallet;
    }
    /**
    * Retrieves an existing HD wallet from session storage using its ID.
    *
    * @param {string} id - Generated when the wallet was initially created
    * @returns {Bip44Wallet} Restored locked Bip44Wallet
    */
    static async restore(id) {
        Bip44Wallet.#isInternal = true;
        if (typeof id !== 'string' || id === '') {
            throw new TypeError('Wallet ID is required to restore');
        }
        const wallet = new this('', undefined, id);
        return wallet;
    }
    /**
    * Derives BIP-44 Nano account private keys.
    *
    * @param {number} index - Index of the account
    * @returns {Promise<Account>}
    */
    async ckd(index) {
        const key = await nanoCKD(this.seed, index);
        return await Account.fromPrivateKey(key, index);
    }
}
/**
* BLAKE2b wallet created by deriving a mnemonic phrase from a seed or vice
* versa. If no value is provided for either, a new BIP-39 seed and mnemonic will
* be generated using a cryptographically strong pseudorandom number generator.
*
* Account private keys are derived on an ad hoc basis using the Blake2b hashing
* function. Account public key are derived from the private key using the
* Ed25519 key algorithm. Account addresses are derived from the public key as
* described in the Nano documentation.
* https://docs.nano.org/integration-guides/the-basics/
*
* A password must be provided when creating or importing the wallet and is used
* to lock and unlock the wallet. The wallet will be initialized as locked. When
* the wallet is unlocked, a new password can be specified using the lock()
* method.
*/
export class Blake2bWallet extends Wallet {
    static #isInternal = false;
    constructor(seed, mnemonic, id) {
        if (!Blake2bWallet.#isInternal) {
            throw new Error(`Blake2bWallet cannot be instantiated directly. Use 'await Blake2bWallet.create()' instead.`);
        }
        super(seed, mnemonic, id);
        Blake2bWallet.#isInternal = false;
    }
    static async create(passkey) {
        Blake2bWallet.#isInternal = true;
        try {
            const seed = new Entropy();
            return await Blake2bWallet.fromSeed(passkey, seed.hex);
        }
        catch (err) {
            throw new Error(`Error creating new Blake2bWallet: ${err}`);
        }
    }
    static async fromSeed(passkey, seed) {
        Blake2bWallet.#isInternal = true;
        if (seed.length !== SEED_LENGTH_BLAKE2B) {
            throw new Error(`Expected a ${SEED_LENGTH_BLAKE2B}-character seed, but received ${seed.length}-character string.`);
        }
        if (!/^[0-9a-fA-F]+$/i.test(seed)) {
            throw new Error('Seed contains invalid hexadecimal characters.');
        }
        const s = seed;
        const m = await Bip39Mnemonic.fromEntropy(seed);
        const wallet = new this(s, m);
        await wallet.lock(passkey);
        return wallet;
    }
    static async fromMnemonic(passkey, mnemonic) {
        Blake2bWallet.#isInternal = true;
        try {
            const m = await Bip39Mnemonic.fromPhrase(mnemonic);
            const s = await m.toBlake2bSeed();
            const wallet = new this(s, m);
            await wallet.lock(passkey);
            return wallet;
        }
        catch (err) {
            throw new Error(`Error importing Blake2bWallet from mnemonic: ${err}`);
        }
    }
    /**
    * Retrieves an existing BLAKE2b wallet from session storage using its ID.
    *
    * @param {string} id - Generated when the wallet was initially created
    * @returns {Blake2bWallet} Restored locked Blake2bWallet
    */
    static async restore(id) {
        Blake2bWallet.#isInternal = true;
        if (typeof id !== 'string' || id === '') {
            throw new TypeError('Wallet ID is required to restore');
        }
        const wallet = new this('', undefined, id);
        return wallet;
    }
    /**
    * Derives BLAKE2b account private keys.
    *
    * @param {number} index - Index of the account
    * @returns {Promise<Account>}
    */
    async ckd(index) {
        const hash = await Tools.blake2b([this.seed, dec.toHex(index, 4)]);
        const key = bytes.toHex(hash);
        return await Account.fromPrivateKey(key, index);
    }
}
/**
* Ledger hardware wallet created by communicating with a Ledger device via ADPU
* calls. This wallet does not feature any seed nor mnemonic phrase as all
* private keys are held in the secure chip of the device. As such, the user
* is responsible for using Ledger technology to back up these pieces of data.
*
* Usage of this wallet is generally controlled by calling functions of the
* `ledger` object. For example, the wallet interface should have a button to
* initiate a device connection by calling `wallet.ledger.connect()`. For more
* information, refer to the ledger.js service file.
*/
export class LedgerWallet extends Wallet {
    static #isInternal = false;
    #ledger;
    get ledger() { return this.#ledger; }
    constructor(ledger) {
        if (!LedgerWallet.#isInternal) {
            throw new Error(`LedgerWallet cannot be instantiated directly. Use 'await LedgerWallet.create()' instead.`);
        }
        super();
        this.#ledger = ledger;
        LedgerWallet.#isInternal = false;
    }
    /**
    * Creates a new Ledger hardware wallet communication layer by dynamically
    * importing the ledger.js service.
    *
    * @returns {LedgerWallet} A wallet containing accounts and a Ledger device communication object
    */
    static async create() {
        LedgerWallet.#isInternal = true;
        const { Ledger } = await import('./ledger.js');
        const l = await Ledger.init();
        return new this(l);
    }
    /**
    * Gets the public key for an account from the Ledger device.
    *
    * @param {number} index - Index of the account
    * @returns {Promise<Account>}
    */
    async ckd(index) {
        const { status, publicKey } = await this.ledger.account(index);
        if (status === 'OK' && publicKey != null) {
            return await Account.fromPublicKey(publicKey, index);
        }
        return null;
    }
    /**
    * Attempts to close the current process on the Ledger device.
    *
    * Overrides the default wallet `lock()` method since as a hardware wallet it
    * does not need to be encrypted by software.
    *
    * @returns True if successfully locked
    */
    async lock() {
        if (this.#ledger == null) {
            return false;
        }
        const result = await this.#ledger.close();
        return result === 'OK';
    }
    /**
    * Attempts to connect to the Ledger device.
    *
    * Overrides the default wallet `unlock()` method since as a hardware wallet it
    * does not need to be encrypted by software.
    *
    * @returns True if successfully unlocked
    */
    async unlock() {
        if (this.#ledger == null) {
            return false;
        }
        const result = await this.#ledger.connect();
        return result === 'OK';
    }
}
